export const M4_UNIT_6 = {
  id: "m4-u6-feelings-moods",
  title: "Unidad 6: How Do You Feel?",
  topic: "Master expressing emotions and moods! Learn to talk about how you feel in different situations using the right vocabulary and grammar. Essential for conversations, medical appointments, and expressing yourself in English!",
  iconType: "heart",
  grammarImage: "/grammar/grammar-m4-u6.jpg",
  grammarText: "Feelings: I FEEL + adjective (general) / I AM + adjective (state) / I'M FEELING + adjective (temporary/changing). Conectores: BECAUSE (razón), BUT (contraste), SO (resultado). Errores: 'I am boring' ❌ → 'I am bored' ✓",
  
  sets: [
    // SET 1: Basic Positive Feelings
    {
      id: "m4-u6-set1",
      title: "Set 1: Positive Emotions",
      questions: [
        {
          type: "match",
          q: "Match the feeling to its meaning:",
          pairs: [
            { left: "happy", right: "feliz/contento" },
            { left: "excited", right: "emocionado" },
            { left: "good", right: "bien" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "'I ___ happy today!'",
          options: ["feel", "am feel", "feeling"],
          a: "feel",
          tip: "'I feel' + adjective = expresar emoción. 'I feel happy' ✓"
        },
        {
          type: "input",
          q: "Complete: 'She ___ excited about her new job.' (be)",
          a: "is"
        },
        {
          type: "order",
          q: "Ordena: 'Me siento bien hoy'",
          words: ["I", "feel", "good", "today."],
          a: "I feel good today."
        },
        {
          type: "mc",
          q: "'I feel happy ___ I got my work permit!'",
          options: ["because", "but", "and"],
          a: "because",
          tip: "'Because' = porque (razón). Explica POR QUÉ te sientes así!"
        },
        {
          type: "input",
          q: "Complete: 'We ___ excited about the weekend.' (be)",
          a: "are"
        },
        {
          type: "mc",
          q: "'He feels great ___ he's tired.'",
          options: ["but", "because", "so"],
          a: "but",
          tip: "'But' = pero (contraste). Dos ideas opuestas: feel great BUT tired."
        },
        {
          type: "order",
          q: "Ordena: 'Estoy emocionado porque voy a ver a mi familia'",
          words: ["I'm", "excited", "because", "I'm", "going", "to", "see", "my", "family."],
          a: "I'm excited because I'm going to see my family.",
          successtip: "✓ FAMILY REUNION! Emotional moment! 👨‍👩‍👧 'Excited because' = expressing emotion + reason. Immigration joy!"
        },
        {
          type: "input",
          q: "Complete: 'I feel good, ___ I'm still learning.' (but/and/because)",
          a: "but"
        },
        {
          type: "mc",
          q: "'She's happy ___ she found a job!'",
          options: ["because", "but", "so"],
          a: "because"
        }
      ]
    },

    // SET 2: Basic Negative Feelings
    {
      id: "m4-u6-set2",
      title: "Set 2: Negative Emotions",
      questions: [
        {
          type: "match",
          q: "Match the feeling to its meaning:",
          pairs: [
            { left: "sad", right: "triste" },
            { left: "angry", right: "enojado" },
            { left: "tired", right: "cansado" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "'I ___ sad today.'",
          options: ["feel", "am feel", "have"],
          a: "feel",
          tip: "'I feel sad' ✓. NOT 'I have sad' ❌. English uses 'feel' or 'am' for emotions!"
        },
        {
          type: "input",
          q: "Complete: 'He ___ angry about the situation.' (be)",
          a: "is"
        },
        {
          type: "order",
          q: "Ordena: 'Estoy cansado porque trabajo mucho'",
          words: ["I'm", "tired", "because", "I", "work", "a", "lot."],
          a: "I'm tired because I work a lot."
        },
        {
          type: "mc",
          q: "ERROR: 'I have sad' - Corrección:",
          options: ["I am sad / I feel sad", "I have sadness", "I sad"],
          a: "I am sad / I feel sad",
          tip: "Spanish 'Tengo tristeza' ≠ English! Use 'I AM sad' or 'I FEEL sad' ✓"
        },
        {
          type: "input",
          q: "Complete: 'I feel sad ___ I miss my country.' (connector)",
          a: "because"
        },
        {
          type: "mc",
          q: "'She's tired, ___ she's going to rest.'",
          options: ["so", "because", "but"],
          a: "so",
          tip: "'So' = entonces (resultado). Tired → SO → going to rest. Cause → effect!"
        },
        {
          type: "order",
          q: "Ordena: 'Él está enojado pero no dice nada'",
          words: ["He's", "angry", "but", "he", "doesn't", "say", "anything."],
          a: "He's angry but he doesn't say anything."
        },
        {
          type: "input",
          q: "Complete: 'We're tired, ___ we need a break.' (so/but/because)",
          a: "so"
        },
        {
          type: "mc",
          q: "'I'm sad ___ I can't go to Shakira's concert next month.'",
          options: ["because", "but", "and"],
          a: "because",
          successtip: "✓ SHAKIRA! Latino heartbreak! 💔 Can't afford concerts = immigrant reality. 'Because' explains why you're sad!"
        }
      ]
    },

    // SET 3: Physical Feelings
    {
      id: "m4-u6-set3",
      title: "Set 3: Physical States",
      questions: [
        {
          type: "match",
          q: "Match physical feeling:",
          pairs: [
            { left: "hungry", right: "con hambre" },
            { left: "thirsty", right: "con sed" },
            { left: "sick", right: "enfermo" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "ERROR: 'I have hungry' - Corrección:",
          options: ["I am hungry", "I have hunger", "I hungry"],
          a: "I am hungry",
          tip: "Spanish 'Tengo hambre' = I HAVE hunger, but English = I AM hungry! Big difference!"
        },
        {
          type: "input",
          q: "Complete: 'She ___ thirsty.' (be)",
          a: "is"
        },
        {
          type: "order",
          q: "Ordena: 'Tengo hambre porque no desayuné'",
          words: ["I'm", "hungry", "because", "I", "didn't", "eat", "breakfast."],
          a: "I'm hungry because I didn't eat breakfast."
        },
        {
          type: "mc",
          q: "ERROR: 'I have cold' - Corrección:",
          options: ["I am cold", "I have coldness", "I cold"],
          a: "I am cold",
          tip: "Spanish 'Tengo frío' ≠ English! 'I AM cold' (temperature) ✓. 'I HAVE a cold' (sick) = different!"
        },
        {
          type: "input",
          q: "Complete: 'I'm sick, ___ I can't go to work.' (so/but/because)",
          a: "so"
        },
        {
          type: "mc",
          q: "'He's hot ___ it's summer.'",
          options: ["because", "so", "but"],
          a: "because"
        },
        {
          type: "order",
          q: "Ordena: 'Estoy enfermo entonces necesito ver a un doctor'",
          words: ["I'm", "sick,", "so", "I", "need", "to", "see", "a", "doctor."],
          a: "I'm sick, so I need to see a doctor.",
          successtip: "✓ HEALTHCARE! Important phrase! 🏥 'I need to see a doctor' = essential for walk-in clinics. No appointment needed!"
        },
        {
          type: "input",
          q: "Complete: 'We're cold ___ the heater isn't working.' (because/but/so)",
          a: "because"
        },
        {
          type: "mc",
          q: "ERROR: 'I have heat' - What's correct?",
          options: ["I am hot", "I have hotness", "I feel heat"],
          a: "I am hot",
          tip: "'I AM hot' (feeling warm) ✓. 'I HAVE heat' ❌. Use 'be' for temperature feelings!"
        }
      ]
    },

    // SET 4: Intermediate Feelings
    {
      id: "m4-u6-set4",
      title: "Set 4: More Emotions",
      questions: [
        {
          type: "match",
          q: "Match feeling to meaning:",
          pairs: [
            { left: "nervous", right: "nervioso" },
            { left: "worried", right: "preocupado" },
            { left: "bored", right: "aburrido (sentirse)" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "ERROR: 'I am boring' - ¿Qué significa esto?",
          options: [
            "'I am boring' = Soy aburrido (personality)",
            "'I am boring' = Estoy aburrido",
            "Ambos significados"
          ],
          a: "'I am boring' = Soy aburrido (personality)",
          tip: "HUGE ERROR! 'I am bored' = estoy aburrido (feeling) ✓. 'I am boring' = soy aburrido (personality) ❌!"
        },
        {
          type: "input",
          q: "Complete: 'She feels ___ about the interview.' (nervous)",
          a: "nervous"
        },
        {
          type: "order",
          q: "Ordena: 'Estoy aburrido porque no tengo nada que hacer'",
          words: ["I'm", "bored", "because", "I", "have", "nothing", "to", "do."],
          a: "I'm bored because I have nothing to do."
        },
        {
          type: "mc",
          q: "'I'm worried ___ my family back home.'",
          options: ["about", "for", "of"],
          a: "about",
          tip: "'Worried ABOUT' = preocupado por. Fixed preposition! 'About' always!"
        },
        {
          type: "input",
          q: "Correct form: 'The movie is boring, so I ___ bored.' (be)",
          a: "am"
        },
        {
          type: "mc",
          q: "'She's nervous, ___ she's ready for the test.'",
          options: ["but", "because", "so"],
          a: "but"
        },
        {
          type: "order",
          q: "Ordena: 'Estoy preocupado por mis papeles de inmigración'",
          words: ["I'm", "worried", "about", "my", "immigration", "papers."],
          a: "I'm worried about my immigration papers.",
          successtip: "✓ IMMIGRATION STRESS! Universal feeling! 📋 'Worried about papers' = every immigrant knows this anxiety!"
        },
        {
          type: "input",
          q: "Complete: 'He's bored, ___ he's watching TV.' (but/so/because)",
          a: "so"
        },
        {
          type: "mc",
          q: "'-ED vs -ING: The class is ___.' (makes you bored)",
          options: ["boring", "bored", "bore"],
          a: "boring",
          successtip: "✓ -ING vs -ED! Key rule! 📚 Thing/person IS boring (causa aburrimiento). You ARE bored (sientes aburrimiento). BORING makes you BORED!"
        }
      ]
    },

    // SET 5: Advanced Emotions (Immigrant Context)
    {
      id: "m4-u6-set5",
      title: "Set 5: Complex Feelings",
      questions: [
        {
          type: "match",
          q: "Match advanced feeling:",
          pairs: [
            { left: "homesick", right: "con nostalgia por casa" },
            { left: "overwhelmed", right: "abrumado" },
            { left: "stressed", right: "estresado" }
          ],
          a: "completed",
          successtip: "✓ IMMIGRANT FEELINGS! Real emotions! 🌍 Homesick, overwhelmed, stressed = universal immigrant experience!"
        },
        {
          type: "mc",
          q: "'I feel homesick ___ I miss my family.'",
          options: ["because", "but", "so"],
          a: "because"
        },
        {
          type: "input",
          q: "Complete: 'She feels ___ with all the responsibilities.' (overwhelmed)",
          a: "overwhelmed"
        },
        {
          type: "order",
          q: "Ordena: 'Me siento abrumado porque tengo demasiadas cosas que hacer'",
          words: ["I", "feel", "overwhelmed", "because", "I", "have", "too", "many", "things", "to", "do."],
          a: "I feel overwhelmed because I have too many things to do."
        },
        {
          type: "mc",
          q: "'I'm stressed about money, ___ I'm working two jobs.'",
          options: ["so", "because", "but"],
          a: "so"
        },
        {
          type: "input",
          q: "Complete: 'He's ___ because he can't find a job.' (frustrated)",
          a: "frustrated"
        },
        {
          type: "mc",
          q: "'I feel homesick, ___ I talk to my family every day.'",
          options: ["so", "but", "because"],
          a: "so",
          tip: "Homesick → SO → talk to family. Emotion causes action!"
        },
        {
          type: "order",
          q: "Ordena: 'Estoy estresado por mi trabajo pero estoy tratando de mantenerme positivo'",
          words: ["I'm", "stressed", "about", "work,", "but", "I'm", "trying", "to", "stay", "positive."],
          a: "I'm stressed about work, but I'm trying to stay positive.",
          successtip: "✓ RESILIENCE! Immigrant strength! 💪 'Stay positive' = mantener esperanza. This mindset = survival!"
        },
        {
          type: "input",
          q: "Complete: 'We feel lonely ___ we don't have friends here yet.' (because/but/so)",
          a: "because"
        },
        {
          type: "mc",
          q: "'She's exhausted ___ she's working 60 hours a week.'",
          options: ["because", "so", "but"],
          a: "because"
        }
      ]
    },

    // SET 6: Feel vs Be vs Feeling (Grammar)
    {
      id: "m4-u6-set6",
      title: "Set 6: Different Ways to Express",
      questions: [
        {
          type: "match",
          q: "Match structure to use:",
          pairs: [
            { left: "I feel happy", right: "General emotion (simple)" },
            { left: "I am happy", right: "Current state (simple)" },
            { left: "I'm feeling better", right: "Temporary/changing (continuous)" }
          ],
          a: "completed",
          tip: "'Feel' and 'am' = similar! 'I'm feeling' = emphasizes change or temporary state!"
        },
        {
          type: "mc",
          q: "General state: 'I ___ happy with my life here.'",
          options: ["am", "am being", "feeling"],
          a: "am"
        },
        {
          type: "input",
          q: "Temporary: 'I ___ better today.' (feel - continuous)",
          a: "am feeling"
        },
        {
          type: "order",
          q: "Ordena: 'Me estoy sintiendo mejor ahora'",
          words: ["I'm", "feeling", "better", "now."],
          a: "I'm feeling better now.",
          tip: "'Feeling better' = mejorando (change happening). Common after being sick!"
        },
        {
          type: "mc",
          q: "'How ___ you feeling today?' (temporary state)",
          options: ["are", "do", "is"],
          a: "are"
        },
        {
          type: "input",
          q: "Complete: 'She ___ sad right now, but she'll be okay.' (be)",
          a: "is"
        },
        {
          type: "mc",
          q: "'I usually ___ good, but today I ___ terrible.'",
          options: ["feel, feel", "am feeling, am feeling", "feel, am feeling"],
          a: "feel, feel",
          tip: "Both can use simple! 'Usually feel' (habit) and 'today I feel' (today's state). Simple works for both!"
        },
        {
          type: "order",
          q: "Ordena: '¿Cómo te sientes?'",
          words: ["How", "do", "you", "feel?"],
          a: "How do you feel?"
        },
        {
          type: "input",
          q: "Alternative question: 'How ___ you feeling?' (continuous)",
          a: "are"
        },
        {
          type: "mc",
          q: "Recovering: 'I was sick yesterday, but I ___ better today.'",
          options: ["am feeling", "feel", "both are correct"],
          a: "both are correct",
          successtip: "✓ BOTH WORK! Flexibility! 🏥 'I feel better' or 'I'm feeling better' = both natural! Continuous emphasizes the change more!"
        }
      ]
    },

    // SET 7: Dialogue Practice Part 1
    {
      id: "m4-u6-set7",
      title: "Set 7: Conversations About Feelings",
      questions: [
        {
          type: "match",
          q: "Match question to context:",
          pairs: [
            { left: "How are you feeling?", right: "Asking about emotions" },
            { left: "What's wrong?", right: "Someone looks upset" },
            { left: "Are you okay?", right: "Checking on someone" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "Friend: 'How are you?' You: 'I ___ tired today.'",
          options: ["am", "have", "feel"],
          a: "am"
        },
        {
          type: "input",
          q: "Boss: 'Are you okay?' You: 'Yes, I'm just ___.' (a little stressed)",
          a: "stressed / a little stressed"
        },
        {
          type: "order",
          q: "Ordena conversación: '¿Qué pasa? Te ves triste.'",
          words: ["What's", "wrong?", "You", "look", "sad."],
          a: "What's wrong? You look sad.",
          successtip: "✓ CARING QUESTION! Canadian culture! 🤝 'What's wrong?' = genuine concern. Canadians check on each other!"
        },
        {
          type: "mc",
          q: "Friend: 'Why are you sad?' You: '___ I miss my family.'",
          options: ["Because", "But", "So"],
          a: "Because"
        },
        {
          type: "input",
          q: "Coworker: 'You look happy!' You: 'Yes! I ___ because I got good news!' (be)",
          a: "am"
        },
        {
          type: "mc",
          q: "Doctor: 'How are you feeling?' You: 'I ___ better than yesterday.'",
          options: ["feel", "am feel", "have"],
          a: "feel"
        },
        {
          type: "order",
          q: "Ordena: 'No me siento bien, creo que estoy enfermo'",
          words: ["I", "don't", "feel", "well,", "I", "think", "I'm", "sick."],
          a: "I don't feel well, I think I'm sick."
        },
        {
          type: "input",
          q: "Friend: 'What's wrong?' You: 'Nothing, I'm just ___.' (tired)",
          a: "tired"
        },
        {
          type: "mc",
          q: "Complete conversation: 'Are you okay?' - 'Not really, I ___ stressed about my visa.'",
          options: ["am", "have", "feeling"],
          a: "am"
        }
      ]
    },

    // SET 8: Dialogue Practice Part 2
    {
      id: "m4-u6-set8",
      title: "Set 8: Extended Conversations",
      questions: [
        {
          type: "mc",
          q: "A: 'How are you?' B: 'I'm okay, ___ a bit tired.'",
          options: ["just", "only", "very"],
          a: "just",
          tip: "'Just' = solo/simplemente. 'Just tired' = downplays, softens. Very Canadian!"
        },
        {
          type: "input",
          q: "A: 'You look worried.' B: 'Yes, I ___ about my job interview tomorrow.' (be)",
          a: "am"
        },
        {
          type: "order",
          q: "Ordena: 'Estoy emocionado porque finalmente conseguí mi permiso de trabajo'",
          words: ["I'm", "excited", "because", "I", "finally", "got", "my", "work", "permit!"],
          a: "I'm excited because I finally got my work permit!",
          successtip: "✓ WORK PERMIT! Life-changing moment! 🎉 This feeling = pure joy! Immigration milestone unlocked!"
        },
        {
          type: "mc",
          q: "A: 'Why are you so happy?' B: '___ I passed my driving test!'",
          options: ["Because", "But", "So"],
          a: "Because"
        },
        {
          type: "input",
          q: "A: 'Are you feeling better?' B: 'Yes, much ___!' (better)",
          a: "better"
        },
        {
          type: "mc",
          q: "A: 'What's wrong?' B: 'I'm homesick. I miss my country, ___ I'm trying to adapt.'",
          options: ["but", "because", "so"],
          a: "but"
        },
        {
          type: "order",
          q: "Ordena: '¿Te sientes bien? Te ves cansado.'",
          words: ["Are", "you", "okay?", "You", "look", "tired."],
          a: "Are you okay? You look tired."
        },
        {
          type: "input",
          q: "A: 'How do you feel about your new job?' B: 'I feel ___, it's perfect!' (great)",
          a: "great"
        },
        {
          type: "mc",
          q: "A: 'You seem stressed.' B: 'I am, ___ I have too many bills to pay.'",
          options: ["because", "but", "so"],
          a: "because"
        },
        {
          type: "order",
          q: "Ordena diálogo completo: 'Estoy nervioso pero también emocionado por mi primera entrevista de trabajo en Canadá'",
          words: ["I'm", "nervous", "but", "also", "excited", "about", "my", "first", "job", "interview", "in", "Canada."],
          a: "I'm nervous but also excited about my first job interview in Canada.",
          successtip: "✓ MIXED EMOTIONS! Real feeling! 😰😊 Nervous BUT excited = totally normal! Both emotions at once = human experience!"
        }
      ]
    },

    // SET 9: Context Integration
    {
      id: "m4-u6-set9",
      title: "Set 9: Real-Life Situations",
      questions: [
        {
          type: "mc",
          q: "At work: 'I feel overwhelmed ___ I'm training for a new position ___ learning English at the same time.'",
          options: ["because, and", "but, so", "so, because"],
          a: "because, and",
          tip: "'Because' = razón. 'And' = añadir otra razón. Double challenge!"
        },
        {
          type: "input",
          q: "Calling in sick: 'I can't come to work today ___ I'm feeling sick.' (because/but/so)",
          a: "because"
        },
        {
          type: "order",
          q: "Ordena: 'Me siento orgulloso porque finalmente hablo inglés con confianza'",
          words: ["I", "feel", "proud", "because", "I", "finally", "speak", "English", "with", "confidence."],
          a: "I feel proud because I finally speak English with confidence.",
          successtip: "✓ PROUD MOMENT! Achievement unlocked! 🏆 From zero to confident speaker = HUGE accomplishment! You should feel proud!"
        },
        {
          type: "mc",
          q: "Immigration office: 'I'm anxious ___ I'm waiting for my permanent residence application.'",
          options: ["because", "but", "so"],
          a: "because"
        },
        {
          type: "input",
          q: "To family back home: 'I feel lonely sometimes, ___ I'm making new friends.' (but/because/so)",
          a: "but"
        },
        {
          type: "mc",
          q: "After payday: 'I'm happy ___ I'm still worried about rent.'",
          options: ["but", "because", "so"],
          a: "but"
        },
        {
          type: "order",
          q: "Ordena: 'Estoy agotado porque trabajo sesenta horas por semana pero lo hago por mi familia'",
          words: ["I'm", "exhausted", "because", "I", "work", "60", "hours", "a", "week,", "but", "I", "do", "it", "for", "my", "family."],
          a: "I'm exhausted because I work 60 hours a week, but I do it for my family.",
          successtip: "✓ SACRIFICE! Immigrant reality! 💪 Exhausted BUT motivated = the duality. Working for family = pure love!"
        },
        {
          type: "input",
          q: "Mental health check-in: 'I feel stressed, ___ I'm trying to take care of myself.' (but/because/so)",
          a: "but"
        },
        {
          type: "mc",
          q: "Therapy/counseling: 'I feel better now ___ I'm talking about my feelings.'",
          options: ["because", "but", "so"],
          a: "because",
          successtip: "✓ MENTAL HEALTH! Important topic! 🧠 Canada has free counseling resources! Talk to someone = okay! Mental health matters!"
        },
        {
          type: "mc",
          q: "Achievement: 'I feel hopeful ___ things are getting better every day.'",
          options: ["because", "but", "so"],
          a: "because"
        }
      ]
    },

    // SET 10: Advanced Integration
    {
      id: "m4-u6-set10",
      title: "Set 10: Complex Emotional Expression",
      questions: [
        {
          type: "mc",
          q: "Multiple emotions: 'I feel happy ___ nervous ___ excited about my citizenship ceremony.'",
          options: ["and, and", "but, and", "because, so"],
          a: "and, and",
          tip: "Multiple emotions at once! 'And' connects all of them. Happy AND nervous AND excited = all true!"
        },
        {
          type: "input",
          q: "Complex feeling: 'I'm grateful for my job, ___ I feel frustrated because I can't use my degree.' (but/so/and)",
          a: "but"
        },
        {
          type: "order",
          q: "Ordena: 'Me siento agradecido por las oportunidades aquí pero extraño mi cultura y mi gente'",
          words: ["I", "feel", "grateful", "for", "the", "opportunities", "here,", "but", "I", "miss", "my", "culture", "and", "my", "people."],
          a: "I feel grateful for the opportunities here, but I miss my culture and my people.",
          successtip: "✓ BITTERSWEET! Immigrant duality! 🌍🇨🇦 Grateful BUT homesick = both feelings valid. This IS the immigrant experience!"
        },
        {
          type: "mc",
          q: "Describing journey: 'I felt scared when I arrived, ___ now I feel confident.'",
          options: ["but", "because", "so"],
          a: "but"
        },
        {
          type: "input",
          q: "Growth: 'I used to feel overwhelmed, but now I ___ stronger.' (feel)",
          a: "feel"
        },
        {
          type: "mc",
          q: "To new immigrant: 'Don't worry if you feel lost. It's normal, ___ it gets better.'",
          options: ["and", "but", "because"],
          a: "and"
        },
        {
          type: "order",
          q: "Ordena: 'Al principio me sentía solo y asustado pero ahora me siento en casa'",
          words: ["At", "first", "I", "felt", "lonely", "and", "scared,", "but", "now", "I", "feel", "at", "home."],
          a: "At first I felt lonely and scared, but now I feel at home.",
          successtip: "✓ TRANSFORMATION! Your journey! 🦋 From scared to 'at home' = the dream! This sentence = every immigrant's story!"
        },
        {
          type: "input",
          q: "Advice: 'If you feel homesick, ___ to your family often.' (talk/talks)",
          a: "talk"
        },
        {
          type: "mc",
          q: "Reflection: 'I feel proud of myself ___ I never gave up.'",
          options: ["because", "but", "so"],
          a: "because"
        },
        {
          type: "mc",
          q: "Final integration: 'I know life here isn't easy, ___ I feel hopeful ___ I'm building a better future, ___ some days I feel tired ___ homesick.'",
          options: [
            "but, because, but, and",
            "and, so, but, or",
            "because, and, so, but"
          ],
          a: "but, because, but, and",
          successtip: "✓ UNIT 6 COMPLETE! Emotional mastery! 🎭 You can now express complex feelings with connectors, avoid 'I have sad' errors, and have real conversations about emotions! From 'I am boring' to 'I feel grateful but homesick' = HUGE growth! You got this! 💪🇨🇦"
        }
      ]
    }
  ],

// ===================================================================
// READING SET: Miguel's Emotional Journey
// ===================================================================
readingSet: {
  id: "m4-u6-reading",
  title: "Reading: Miguel's Emotional Journey",
  category: "reading",

  questions: [
    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 1: The Arrival (preguntas 1-2)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**First Impressions**

My name is Miguel and I want to talk about my feelings as a new immigrant in Canada. When I first arrived two years ago, I felt scared and overwhelmed. Everything was new and different. 

I felt homesick because I missed my family, my food, and my language. Some days I felt so lonely that I just wanted to go back home.`
    },
    {
      type: "mc",
      q: "¿Cómo se sentía Miguel cuando llegó a Canadá por primera vez?",
      options: ["Scared and overwhelmed", "Happy and excited", "Angry and frustrated"],
      a: "Scared and overwhelmed",
      successtip: "✓ INITIAL FEELINGS!\nIt is normal to feel overwhelmed when everything is new. 🇨🇦"
    },
    {
      type: "mc",
      q: "¿Por qué se sentía solo (lonely) Miguel?",
      options: [
        "Because he missed his family, food, and language",
        "Because he didn't have a job",
        "Because he was sick"
      ],
      a: "Because he missed his family, food, and language",
      successtip: "✓ HOMESICK!\nMissing your culture and family causes that lonely feeling. 💔"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 2: The Struggle & Small Wins (preguntas 3 y 5)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Starting to Work**

But I didn't give up. I started working at a warehouse, and even though I felt tired every day, I also felt proud because I was earning money. 

I felt nervous about speaking English, but I practiced every day. My coworkers were kind, so I felt more confident over time.`
    },
    {
      type: "mc",
      q: "¿Qué sintió Miguel cuando empezó a trabajar en el almacén (warehouse)?",
      options: [
        "Tired but proud",
        "Only tired",
        "Happy and relaxed"
      ],
      a: "Tired but proud",
      tip: "'Tired BUT proud' = two emotions! Exhausted from work BUT proud of earning money!",
      successtip: "✓ COMPLEX EMOTIONS!\nYou can feel two things at the same time. 🏗️💪"
    },
    {
      type: "mc",
      q: "¿Cómo evolucionó el sentimiento de Miguel sobre hablar inglés?",
      options: ["Nervous at first, then more confident", "Always confident", "Always scared"],
      a: "Nervous at first, then more confident",
      successtip: "✓ CONFIDENCE!\nPractice and kind people help you feel better when speaking. 🗣️✨"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 3: Current Situation (preguntas 6-7)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Two Years Later**

Now, I feel different. I still feel homesick sometimes, especially during holidays, but I also feel grateful for the opportunities here. 

I feel excited because my family is coming next year! I feel less stressed about money because I have a better job now. And I feel hopeful about my future.`
    },
    {
      type: "mc",
      q: "¿Por qué Miguel se siente emocionado (excited) en este momento?",
      options: [
        "Because his family is coming next year",
        "Because he bought a house",
        "Because he graduated"
      ],
      a: "Because his family is coming next year",
      successtip: "✓ FAMILY REUNIFICATION!\nThe dream of many immigrants is coming true for him! 👨‍👩‍👧‍👦"
    },
    {
      type: "mc",
      q: "¿Cuándo es más común que Miguel todavía se sienta 'homesick'?",
      options: ["During holidays", "Every day", "Never anymore"],
      a: "During holidays",
      successtip: "✓ HOLIDAYS!\nChristmas or birthdays are the hardest times to be away. 🎄"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 4: Reflection & Connectors (preguntas 4, 8, 9, 10)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**A Message of Hope**

Some days I feel exhausted because I work so much, and some days I feel frustrated because I can't use my university degree yet. But most days, I feel proud of how far I've come. 

I feel strong because I survived the hardest part. To other immigrants: it's okay to feel sad, scared, or overwhelmed. These feelings are normal. But remember, you're also brave, and things do get better!`
    },

    

    {
      type: "mc",
      q: "En todo el texto, Miguel usa el conector 'because' frecuentemente para:",
      options: ["Explicar la razón de sus emociones", "Hacer una pregunta", "Hablar del pasado"],
      a: "Explicar la razón de sus emociones",
      tip: "'Because' (porque) nos ayuda a dar el motivo de un sentimiento. 💡",
      successtip: "✓ CONNECTOR MASTER!\nBecause = Reason. Very important for expressing yourself! 🎯"
    },
    {
      type: "mc",
      q: "Miguel usa la palabra 'but' muchas veces para:",
      options: [
        "Contrastar emociones negativas con positivas",
        "Dar razones",
        "Añadir información"
      ],
      a: "Contrastar emociones negativas con positivas",
      tip: "'But' muestra contraste: tired BUT proud, homesick BUT grateful. 🔄",
      successtip: "✓ CONTRAST!\n'But' (pero) ayuda a mostrar que hay esperanza a pesar de los problemas. ⚖️"
    },
    {
      type: "mc",
      q: "¿Qué mensaje final le da Miguel a otros inmigrantes?",
      options: [
        "It's okay to feel sad/scared, you're brave, things get better",
        "Don't feel sad",
        "Go back home if you're sad"
      ],
      a: "It's okay to feel sad/scared, you're brave, things get better",
      successtip: "✓ ENCOURAGEMENT!\nMiguel wants others to know they are brave. 🦁"
    },
    {
      type: "mc",
      q: "En conclusión, el objetivo principal de esta lectura es demostrar:",
      options: [
        "Cómo expresar emociones complejas usando conectores",
        "Solo sentimientos negativos",
        "Solo sentimientos positivos"
      ],
      a: "Cómo expresar emociones complejas usando conectores",
      successtip: "✓ READING COMPLETE! 🎉\nHas dominado el vocabulario de sentimientos y cómo conectarlos con lógica. ¡Excelente progreso! 🏆"
    }
  ]
},
  // REVIEW SET
  reviewSet: {
    id: "m4-u6-review",
    title: "Set 12: Unit Review",
    category: "review",
    questions: [
      {
        type: "match",
        q: "Match connector to use:",
        pairs: [
          { left: "because", right: "razón (why)" },
          { left: "but", right: "contraste" },
          { left: "so", right: "resultado" }
        ],
        a: "completed"
      },
      {
        type: "mc",
        q: "ERROR común: 'I have sad' debe ser:",
        options: ["I am sad / I feel sad", "I have sadness", "I sad"],
        a: "I am sad / I feel sad"
      },
      {
        type: "mc",
        q: "ERROR: 'I am boring' significa:",
        options: [
          "Soy aburrido (personality) - WRONG meaning!",
          "Estoy aburrido (feeling)",
          "Ambos"
        ],
        a: "Soy aburrido (personality) - WRONG meaning!",
        tip: "Correct = 'I am BORED' (feeling)! -ED = you feel it. -ING = you cause it!"
      },
      {
        type: "input",
        q: "Physical feeling: 'I ___ hungry.' (be)",
        a: "am"
      },
      {
        type: "mc",
        q: "'I feel sad ___ I miss my family.'",
        options: ["because", "but", "so"],
        a: "because"
      },
      {
        type: "order",
        q: "Ordena: 'Estoy cansado pero feliz'",
        words: ["I'm", "tired", "but", "happy."],
        a: "I'm tired but happy."
      },
      {
        type: "mc",
        q: "Temporary improvement: 'I ___ better today.'",
        options: ["am feeling / feel", "have", "am"],
        a: "am feeling / feel",
        tip: "Both work! 'Feel' or 'am feeling' for temporary state!"
      },
      {
        type: "input",
        q: "Question: 'How ___ you feel?' (do/are/is)",
        a: "do"
      },
      {
        type: "mc",
        q: "'The movie is boring' means:",
        options: [
          "The movie causes boredom (-ING = causa)",
          "The movie feels bored",
          "The movie is a person"
        ],
        a: "The movie causes boredom (-ING = causa)"
      },
      {
        type: "mc",
        q: "Final integration: 'I feel homesick ___ I miss my country, ___ I'm grateful ___ I have opportunities here, ___ I'm building a better future.'",
        options: [
          "because, but, because, and",
          "and, so, but, because",
          "so, because, and, but"
        ],
        a: "because, but, because, and",
        successtip: "✓ UNIT 6 MASTERED! 🎭 You can now express any emotion correctly with proper connectors! No more 'I have sad' or 'I am boring'! From basic 'I feel happy' to complex 'I feel homesick but grateful' = fluent emotional expression! This is real English! 💬💪🇨🇦"
      }
    ]
  }
};
